import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Plus, Eye } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { SupplierEvaluationDialog } from "./SupplierEvaluationDialog";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const SupplierEvaluations = () => {
  const { t } = useLanguage();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedEvaluation, setSelectedEvaluation] = useState<any>(null);

  const { data: evaluations, isLoading, refetch } = useQuery({
    queryKey: ['supplier-evaluations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('supplier_evaluations')
        .select(`
          *,
          suppliers!inner (
            id,
            name
          )
        `)
        .order('evaluation_date', { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });

  const getRecommendationBadge = (recommendation: string) => {
    if (recommendation === 'approved') {
      return <Badge className="bg-green-500">{t('approved')}</Badge>;
    } else if (recommendation === 'conditional') {
      return <Badge className="bg-yellow-500">{t('conditional')}</Badge>;
    } else {
      return <Badge variant="destructive">{t('notApproved')}</Badge>;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">{t('supplierEvaluations')}</h3>
          <p className="text-sm text-muted-foreground">ISO 9001 {t('supplierEvaluations')}</p>
        </div>
        <Button onClick={() => { setSelectedEvaluation(null); setIsDialogOpen(true); }}>
          <Plus className="mr-2 h-4 w-4" />
          {t('evaluateSupplier')}
        </Button>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('supplier')}</TableHead>
              <TableHead>{t('evaluationDate')}</TableHead>
              <TableHead>{t('evaluationPeriod')}</TableHead>
              <TableHead>{t('overallScore')}</TableHead>
              <TableHead>{t('recommendation')}</TableHead>
              <TableHead className="text-right">{t('actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center">{t('loading')}</TableCell>
              </TableRow>
            ) : evaluations?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center">{t('noData')}</TableCell>
              </TableRow>
            ) : (
              evaluations?.map((evaluation) => (
                <TableRow key={evaluation.id}>
                  <TableCell className="font-medium">{evaluation.suppliers.name}</TableCell>
                  <TableCell>{new Date(evaluation.evaluation_date).toLocaleDateString()}</TableCell>
                  <TableCell>{t(evaluation.evaluation_period.toLowerCase())}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{Number(evaluation.overall_score).toFixed(1)}/5</Badge>
                  </TableCell>
                  <TableCell>{getRecommendationBadge(evaluation.recommendation)}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setSelectedEvaluation(evaluation); setIsDialogOpen(true); }}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <SupplierEvaluationDialog
        open={isDialogOpen}
        onClose={() => { setIsDialogOpen(false); setSelectedEvaluation(null); refetch(); }}
        evaluation={selectedEvaluation}
      />
    </div>
  );
};